import InputWrapperUI from '@axo/shared/ui/input/lib/InputWrapper/InputWrapperUI';
import { classNames } from '@axo/shared/util/dom';
import React, { FocusEventHandler, useEffect, useMemo, useState } from 'react';
import { IInputPrimaryState } from '../../models/input.model';
import DropdownUI from '../Dropdown/DropdownUI';
import { IInputInfoBox } from '../InputInfoBox';
import styles from './DateDropdownUI.module.scss';
import {
  filterMonths,
  generateMonthsOptions,
  generateYearOptions,
  getDate,
  getMonthWithFallback,
} from './DateDropdownUtils';

export interface IDateDropdownBase {
  minYear: number;
  maxYear: number;
  minYearSuffix?: string;
  maxYearSuffix?: string;
  latestYearAtTop: boolean;
  monthPlaceholder?: string;
  yearPlaceholder?: string;
  showYearOnly?: boolean;
  disableMonthsUntilYear?: number;
  i18n: string;
  disableDatesInFuture?: boolean;
  disableDatesInPast?: boolean;
  disabled?: boolean;
  label?: string;
  labelYearOnly?: string;
  state?: IInputPrimaryState;
}

export interface IOption {
  value: number;
  text: string;
  disabled?: boolean;
}

export type IMonthsOptions = Array<IOption>;
export type IYearOptions = Array<IOption>;

export type ISelectedDate = {
  year?: number;
  month?: number;
  date?: string;
};

export interface IDateDropdown extends IDateDropdownBase {
  value?: string;
  onBlur?: FocusEventHandler<HTMLDivElement>;
  onFocus?: FocusEventHandler<HTMLDivElement>;
  onChange: (value: string | undefined) => void;
  infoBox?: IInputInfoBox;
}

export const DateDropdownUI = ({
  i18n,
  minYear,
  maxYear,
  latestYearAtTop,
  minYearSuffix,
  maxYearSuffix,
  disableDatesInFuture,
  disableDatesInPast,
  monthPlaceholder,
  yearPlaceholder,
  showYearOnly = false,
  disableMonthsUntilYear,
  value,
  onFocus,
  onChange,
  onBlur,
  disabled,
  label,
  labelYearOnly,
  state,
  infoBox,
  ...props
}: IDateDropdown) => {
  const [selectedYear, setSelectedYear] = useState<number | undefined>(
    value ? new Date(value).getFullYear() : undefined
  );
  const [selectedMonth, setSelectedMonth] = useState<number | undefined>(
    value ? new Date(value).getMonth() : undefined
  );

  const [isYearOnly, setIsYearOnly] = useState<boolean>(showYearOnly);

  useEffect(() => {
    setIsYearOnly(
      getIsYearOnly({
        showYearOnly,
        disableMonthsUntilYear,
        selectedYear,
      })
    );
  }, [showYearOnly, disableMonthsUntilYear, selectedYear]);

  useEffect(() => {
    if (value) {
      const date = new Date(value);
      const month = date.getMonth();
      const year = date.getFullYear();

      if (!disableMonthsUntilYear || disableMonthsUntilYear <= year) {
        setSelectedMonth(month);
      }
      setSelectedYear(year);
    }
  }, [value, disableMonthsUntilYear]);

  const monthsOptions = useMemo(() => {
    const months = generateMonthsOptions(i18n);

    if ((disableDatesInFuture || disableDatesInPast) && selectedYear) {
      return (
        filterMonths({
          months,
          year: selectedYear,
          filter: disableDatesInFuture ? 'past' : 'future',
        }) || months
      );
    }
    return months;
  }, [selectedYear, disableDatesInFuture, disableDatesInPast, i18n]);

  const yearOptions = useMemo(
    () =>
      generateYearOptions(
        minYear,
        maxYear,
        latestYearAtTop,
        minYearSuffix,
        maxYearSuffix
      ),
    [minYear, maxYear, latestYearAtTop, minYearSuffix, maxYearSuffix]
  );

  interface IsYearOnly {
    showYearOnly?: boolean;
    disableMonthsUntilYear?: number;
    selectedYear?: number;
  }

  const getIsYearOnly = ({
    showYearOnly,
    disableMonthsUntilYear,
    selectedYear,
  }: IsYearOnly): boolean => {
    if (showYearOnly) {
      return true;
    }

    if (disableMonthsUntilYear && selectedYear) {
      return disableMonthsUntilYear > selectedYear;
    }

    return false;
  };

  const handleYearChange: React.ChangeEventHandler<HTMLSelectElement> = (e) => {
    const updatedYear = parseInt(e.target.value);
    setSelectedYear(updatedYear);

    const isYearOnly = getIsYearOnly({
      showYearOnly,
      disableMonthsUntilYear,
      selectedYear: updatedYear,
    });

    setIsYearOnly(isYearOnly);

    if (isYearOnly) {
      const date = getDate(updatedYear, 0);
      onChange(date);
      return;
    }

    const filteredMonths = filterMonths({
      months: generateMonthsOptions(i18n),
      year: updatedYear,
      filter: disableDatesInFuture ? 'past' : 'future',
    });

    const updatedMonth =
      selectedMonth !== undefined
        ? getMonthWithFallback(
            filteredMonths,
            selectedMonth,
            new Date().getMonth()
          )
        : undefined;

    const newDate =
      updatedYear && updatedMonth !== undefined
        ? getDate(updatedYear, updatedMonth)
        : undefined;

    setSelectedMonth(updatedMonth);
    if (newDate !== value) {
      onChange(newDate);
    }
  };

  const handleMonthChange: React.ChangeEventHandler<HTMLSelectElement> = (
    e
  ) => {
    const updatedMonth = parseInt(e.target.value);

    const newDate =
      selectedYear && updatedMonth !== undefined
        ? getDate(selectedYear, updatedMonth)
        : undefined;

    setSelectedMonth(updatedMonth);
    if (newDate !== value) {
      onChange(newDate);
    }
  };

  return (
    <div className={styles.flexGrow}>
      <InputWrapperUI
        label={isYearOnly && labelYearOnly ? labelYearOnly : label}
      >
        <div
          onFocus={onFocus}
          onBlur={onBlur}
          className={classNames(styles.container, isYearOnly && styles.single)}
        >
          <DropdownUI
            value={selectedYear?.toString() || ''}
            options={yearOptions}
            name="years"
            onChange={handleYearChange}
            disabled={disabled}
            placeholder={yearPlaceholder}
            aria-label="years"
            state={state}
            infoBox={infoBox}
            {...props}
          />

          {!isYearOnly && (
            <DropdownUI
              value={selectedMonth?.toString() || ''}
              options={monthsOptions}
              name="months"
              onChange={handleMonthChange}
              disabled={disabled}
              placeholder={monthPlaceholder}
              aria-label="months"
              state={state}
              {...props}
            />
          )}
        </div>
      </InputWrapperUI>
    </div>
  );
};

export default DateDropdownUI;
